import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { constructPlannerReservationHref } from "../../../../common/infrastructure/routes/routePaths";
import { getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";

interface IReservationSearchForm {
  reservationId: string;
}

export const useReservationSearch = () => {
  const navigate = useNavigate();
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [searchReservationId, setSearchReservationId] = useState<number | null>(
    null
  );

  const searchFormMethods = useForm<IReservationSearchForm>({
    defaultValues: { reservationId: "" },
  });

  // Search reservation query - only runs when searchReservationId is set
  const {
    data: searchResultEventId,
    isFetching,
    isError: isSearchError,
    isSuccess,
  } = useApiQuery<number | null>({
    queryKey: [
      endpointsMetadata.planner.getEventIdByReservationId,
      searchReservationId,
    ],
    endpoint: `${endpointsMetadata.planner.getEventIdByReservationId}?reservationId=${searchReservationId}`,
    enabled: searchReservationId !== null && searchReservationId > 0,
    globalIsLoading: false,
    disableErrorToast: true,
    retry: false,
  });

  // Only show loading when we're actually searching
  const isSearching = isFetching && searchReservationId !== null;

  // Handle search result
  useEffect(() => {
    if (searchReservationId === null || isFetching) return;

    if (isSearchError) {
      getErrorToast("Възникна грешка при търсенето.");
      setSearchReservationId(null);
      return;
    }

    if (isSuccess) {
      if (searchResultEventId && typeof searchResultEventId === "number") {
        setIsSearchDialogOpen(false);
        searchFormMethods.reset();
        navigate(
          constructPlannerReservationHref(
            searchResultEventId,
            searchReservationId
          )
        );
        setSearchReservationId(null);
      } else {
        // Query succeeded but returned null/0/undefined - reservation not found
        getErrorToast("Резервацията не беше открита.");
        setSearchReservationId(null);
      }
    }
  }, [
    searchResultEventId,
    isFetching,
    isSearchError,
    isSuccess,
    searchReservationId,
    navigate,
    searchFormMethods,
  ]);

  const handleSearchReservation = useCallback(
    (data: IReservationSearchForm) => {
      const reservationId = parseInt(data.reservationId, 10);
      if (isNaN(reservationId) || reservationId <= 0) {
        getErrorToast("Моля въведете валиден номер на резервация.");
        return;
      }
      setSearchReservationId(reservationId);
    },
    []
  );

  const openSearchDialog = useCallback(() => {
    setIsSearchDialogOpen(true);
  }, []);

  const closeSearchDialog = useCallback(() => {
    setIsSearchDialogOpen(false);
    searchFormMethods.reset();
    setSearchReservationId(null);
  }, [searchFormMethods]);

  return {
    isSearchDialogOpen,
    openSearchDialog,
    closeSearchDialog,
    searchFormMethods,
    handleSearchReservation,
    isSearching,
  };
};
